import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import UserBadge from './UserBadge';

const localeCurrencyMap = {
  'en-ZA': 'ZAR',
  'en-US': 'USD',
  'en-GB': 'GBP',
  'fr-FR': 'EUR',
};

const GameDetails = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();

  const [game, setGame] = useState(null);
  const [rsvps, setRsvps] = useState({ attending: [], reserves: [], notAttending: [] });
  const [userRsvp, setUserRsvp] = useState(null);
  const [cutoffPassed, setCutoffPassed] = useState(false);
  const [cutoffText, setCutoffText] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [maxPlayers, setMaxPlayers] = useState(0);
  const [teamsExist, setTeamsExist] = useState(false);
  const authToken = localStorage.getItem('authToken');
  const userId = authToken ? JSON.parse(atob(authToken.split('.')[1])).id : null;

  let userLocale = navigator.language || 'en-ZA';
  let userCurrency = localeCurrencyMap[userLocale] || 'ZAR';

  if (Intl.DateTimeFormat().resolvedOptions().timeZone.includes('Africa/Johannesburg')) {
    userLocale = 'en-ZA';
    userCurrency = 'ZAR';
  }

  useEffect(() => {
    if (!authToken) {
      alert('Not logged in');
      return;
    }

    const fetchData = async () => {
      try {
        const [gameRes, teamRes, teamsRes] = await Promise.all([
          fetch(`/api/games/${gameId}`, {
            headers: { Authorization: `Bearer ${authToken}` }
          }),
          fetch(`/api/games/${gameId}/team`, {
            headers: { Authorization: `Bearer ${authToken}` }
          }),
          fetch(`/api/game-teams/${gameId}`, {
            headers: { Authorization: `Bearer ${authToken}` }
          })
        ]);

        if (!gameRes.ok || !teamRes.ok) throw new Error('Failed to fetch game or team info');

        const gameData = await gameRes.json();
        const teamData = await teamRes.json();

        const fetchedGame = gameData.game || gameData;
        setGame(fetchedGame);
        setMaxPlayers(fetchedGame.max_players);
        setIsAdmin(gameData.isAdmin);
        setRsvps({
          attending: teamData.mainTeam || [],
          reserves: teamData.reserves || [],
          notAttending: teamData.notAttending || []
        });

        if (teamsRes.ok) {
          const teamsData = await teamsRes.json();
          setTeamsExist(teamsData.teamsExist);
        }

        const cutoff = fetchedGame.rsvp_cutoff || fetchedGame.cutoff_hours;
        if (cutoff && fetchedGame.game_datetime) {
          const gameTime = new Date(fetchedGame.game_datetime).getTime();
          const cutoffTime = gameTime - cutoff * 3600000;
          const now = Date.now();

          if (now > cutoffTime) {
            setCutoffPassed(true);
            setCutoffText('⛔ RSVP deadline to opt out has passed.');
          } else {
            const diff = cutoffTime - now;
            const days = Math.floor(diff / 86400000);
            const hours = Math.floor((diff % 86400000) / 3600000);
            const minutes = Math.floor((diff % 3600000) / 60000);

            const dayText = days > 0 ? `${days} day${days > 1 ? 's' : ''}` : '';
            const hourText = hours > 0 ? `${hours} hour${hours > 1 ? 's' : ''}` : '';
            const minuteText = `${minutes} min${minutes !== 1 ? 's' : ''}`;

            let cutoffString = '⏳ RSVP cutoff in ';
            if (days > 0) {
              cutoffString += `${dayText} ${hourText} ${minuteText}`;
            } else if (hours > 0) {
              cutoffString += `${hourText} ${minuteText}`;
            } else {
              cutoffString += `${minuteText}`;
            }

            setCutoffText(cutoffString.trim());
          }
        }

        const combinedUsers = [
          ...(teamData.mainTeam || []),
          ...(teamData.reserves || []),
          ...(teamData.notAttending || [])
        ];

        const existing = combinedUsers.find(r => r.user_id === userId);
        if (existing) {
          setUserRsvp(existing.status);
        }
      } catch (err) {
        console.error(err);
        alert('Could not load game or RSVPs');
      }
    };

    fetchData();
  }, [gameId, authToken, userId]);

  const handleRsvp = (status) => {
    if (cutoffPassed && status === 'out') return;

    fetch('/api/rsvps', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`
      },
      body: JSON.stringify({ gameId, status })
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to RSVP');
        return res.json();
      })
      .then(() => window.location.reload())
      .catch(err => {
        console.error(err);
        alert('Could not submit RSVP');
      });
  };

  const formatCurrency = (amount, currency = 'ZAR', locale ='en-ZA') => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  if (!game) return <div style={styles.loading}>Loading game details...</div>;

  const gameDate = new Date(game.game_datetime);
  const formattedDate = gameDate.toLocaleDateString(userLocale, { day: 'numeric', month: 'long', year: 'numeric' });
  const formattedTime = gameDate.toLocaleTimeString(userLocale, { hour: '2-digit', minute: '2-digit', hour12: false });

  const showTeamsButton = isAdmin || teamsExist;
  const teamsDestination = isAdmin
    ? `/games/${gameId}/make-teams`
    : `/games/${gameId}/view-teams`;

  return (
    <div style={styles.container}>
      <UserBadge />
      <h2>{game.description}</h2>
      {game.opponent && <p><strong>Opponent:</strong> {game.opponent}</p>}
      <p><strong>Date:</strong> {formattedDate}</p>
      <p><strong>Time:</strong> {formattedTime}</p>

      {cutoffText && (
        <div style={cutoffPassed ? styles.cutoffPassed : styles.cutoffWarning}>
          {cutoffText}
        </div>
      )}

      {game.venue && <p><strong>Venue:</strong> {game.venue}</p>}
      {game.colour && <p><strong>Colour:</strong> {game.colour}</p>}
      {game.cost !== null && <p><strong>Cost:</strong> {formatCurrency(game.cost, userCurrency, userLocale)}</p>}
      {game.notes && <p><strong>Notes:</strong> {game.notes}</p>}

      <h3>Team Sheet</h3>
      <strong>Main Team</strong>
      <ol>
        {rsvps.attending.map(user => (
          <li key={user.user_id}>{user.name} {user.surname}</li>
        ))}
      </ol>

      <strong>Reserves</strong>
      <ol>
        {rsvps.reserves.map(user => (
          <li key={user.user_id}>{user.name} {user.surname}</li>
        ))}
      </ol>

      <div style={styles.buttonGroup}>
        <button
          onClick={() => handleRsvp('in')}
          disabled={userRsvp === 'in'}
          style={styles.rsvpButton}
        >
          ✅ I'm in
        </button>
        <button
          onClick={() => handleRsvp('out')}
          disabled={userRsvp === 'out' || cutoffPassed}
          style={styles.rsvpButton}
        >
          ❌ I'm out
        </button>
      </div>

      <div style={styles.buttonGroup}>
        <button onClick={() => navigate(-1)} style={styles.backButton}>⬅ Back</button>
        {showTeamsButton && (
          <button onClick={() => navigate(teamsDestination)} style={styles.adminButton}>
            👥 Teams
          </button>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: { padding: '20px', maxWidth: '700px', margin: '0 auto' },
  loading: { padding: '20px', textAlign: 'center' },
  buttonGroup: { marginTop: '20px' },
  rsvpButton: { marginRight: '10px', padding: '8px 16px' },
  backButton: { padding: '8px 16px', marginRight: '10px' },
  adminButton: { padding: '8px 16px', marginRight: '10px' },
  cutoffWarning: { backgroundColor: '#fff3cd', color: '#856404', padding: '8px', margin: '10px 0' },
  cutoffPassed: { backgroundColor: '#f8d7da', color: '#721c24', padding: '8px', margin: '10px 0' }
};

export default GameDetails;
